// The Studio's words in English: the source catalog. Every other catalog
// has each key of this one, with the same `{placeholders}`
// (test/i18n.test.ts). A sentence stays whole; what it carries is a
// placeholder, so another language puts it where its own word order wants it.
//
// Keys ending in `.html` (and the credits) hold markup written in this
// repository: a page puts them in as they are. Every other value is text.

export const EN = {
  // The room's bar, list and shelf.
  "room.back": "Back to the room",
  "room.home": "Pocket Studio home",
  "room.loading": "Opening the studio",
  "room.hint": "Pick a device, a game, the laptop or the TV. Drag to look around.",
  "room.index": "Everything in the room",
  "room.shelf.previous": "Previous games",
  "room.shelf.next": "More games",
  "room.shelf.pages": "Pages of the game shelf",
  "room.shelf.page": "Page {page} of {pages}",
  "room.closet": "Closet",
  "room.settings": "Settings",
  "room.account": "Account",
  "room.laptop": "Laptop",
  "room.notOpen": "The room is not open yet.",

  // A device's card, a game's card and their rows.
  "room.play": "Play",
  "room.playOn": "Play on {device}",
  "room.open": "Open",
  "room.download": "Download {title} for the {device}",
  "room.package.for": "For {device}",
  "room.colour.group": "{device} colour",
  "room.colour.locked": "{colour}, for members",
  "room.colour.own": "Your own colour",
  "room.colour.own.locked": "Your own colour, for members",
  "device.lead": "Pick a game to play on it.",
  "device.empty": "Nothing on the shelf runs on this one yet.",
  "closet.lead": "Pick a colour for each device.",
  "closet.own": "Your own, {color}",
  "room.by": "By {author}",
  "room.verified": "Verified by Pocket Nexus",
  "room.remixOf": "A remix of {title} by {author}",
  "room.homebrew": "Its packages are homebrew from the author, not tested by Pocket Nexus.",
  "room.howToInstall": "How to install a package",
  "room.remix": "Remix",
  "room.keep": "Put it on my shelf",
  "room.keep.note": "It stays on your shelf until you take it off.",
  "room.unkeep": "Take it off my shelf",
  "room.kept": "It is on your shelf.",
  "room.unkept": "It is off your shelf.",
  "room.site.play": "Play in the browser",
  "room.site.empty.verified": "Nothing to play yet. Upload its web build and packages from a terminal.",
  "room.site.empty": "Nothing to play yet. Upload its packages from a terminal.",
  "room.noBuild": "No build yet.",
  "room.copy": "Copy",
  "room.copied": "Copied",
  "room.save": "Save",
  "room.title": "Title",
  "room.address": "Address",
  "room.address.change": "Change the address",
  "room.address.failed": "Could not change the address. Try again.",
  "room.takedown": "Pocket Nexus took this game down",
  "room.takedown.body": "It is off the shelf and its address, and it cannot be published until that is lifted. Write to {email}.",
  "room.publish": "Publish",
  "room.publish.note": "Anyone with its address can play it. You can take it down again.",
  "room.unpublish": "Take it down",
  "room.members.download": "Allow downloads",
  "room.packages": "Packages: {list}.",
  "room.homebrew.own": "Players see your packages as homebrew, not tested by Pocket Nexus.",
  "room.notDiscoverable": "It is not on Pocket TV or in search. Members' games are.",
  "room.rename": "Rename",
  "room.rename.failed": "Could not rename it. Try again.",
  "room.delete": "Delete this game",
  "room.delete.confirm": "Delete it for good",
  "room.delete.keep": "Keep it",
  "room.delete.failed": "Could not delete it. Try again.",
  "room.changeFailed": "Could not change that.",

  // Playing on a device.
  "play.title": "Playing",
  "play.lead.both": "Use the keys, click its buttons, or touch the screen.",
  "play.lead.touch": "Touch the screen with your pointer.",
  "play.lead.keys": "Use the keys or click its buttons.",
  "keys.arrows": "Arrows",
  "keys.dpad": "D-pad",
  "keys.space": "Space",
  "keys.leftRight": "Left / Right",
  "keys.circle": "Circle",
  "keys.cross": "Cross",
  "keys.squareTriangle": "Square / Triangle",

  // Pocket TV's remote.
  "tv.lead": "What people are making, one game at a time.",
  "tv.previous": "Previous",
  "tv.next": "Next",
  "tv.open": "Open this game",
  "tv.soundOn": "Turn the sound on",
  "tv.soundOff": "Turn the sound off",
  "tv.change": "Change the game",
  "tv.openIt": "Open it",

  // The link code of the remix dialog, and the prompt for making a game with
  // a coding agent. "Link code:" stays in English in every language's prompt:
  // the make-game skill reads the code after it.
  "agent.linking": "Making a link code",
  "agent.linkFailed": "Could not make a link code. Try again.",
  // The prompt that remixes a game, for any coding agent (remixPrompt in
  // ../agents.ts). The first is for a game with a public repository, the
  // second for a Pocket app, whose source the Studio holds. The command and
  // the file names in them stay as they are in every language.
  "remix.title": "Remix {title}",
  "remix.prompt": "Remix the game \"{title}\" ({page}) into a game of my own.\n\n1. Clone its source with its submodules: {repository}\n2. Read its README.md and AGENTS.md. Build it and run it on this computer.\n3. Change it: {game}\n\nWhen I ask you to publish it on Pocket Studio, follow {guide}",
  "remix.prompt.pocket": "Remix the Pocket Studio game \"{title}\" ({page}) into a game of my own.\n\n1. It is a PocketJS app whose source Pocket Studio holds. Follow {guide} to set this computer up and link it to my account. Ask me for the link code.\n2. Copy the game: pocket-studio remix {id}\n3. Change it: {game}",

  // The sign-in wall and gate.
  "wall.make": "Sign in to make an app, and your room and your apps come with you.",
  "gate.title": "Your studio is ready",
  "gate.lead": "Sign in to keep your room and your games.",

  // Toasts.
  "toast.noBuild": "That game has no build for a device yet.",
  "toast.noBuildHere": "That game has no build for this device yet.",
  "toast.colourFailed": "Could not change the colour.",
  "toast.roomMembers": "{room} comes with a membership.",
  "toast.roomMembers.unnamed": "That room comes with a membership.",
  "toast.roomFailed": "Could not change the room.",
  "toast.downloading": "{title} for the {device} is downloading.",
  "toast.downloading.unnamed": "The game for the {device} is downloading.",
  "toast.downloadUnavailable": "That download is not available.",
  "toast.startFailed": "That game could not start.",
  "checkout.success": "Thank you. Your membership is starting.",
  "checkout.member": "You are a member. Every colour is yours.",
  "checkout.member.page": "You are a member. Past versions are yours to download.",
  "checkout.cancelled": "The payment was not finished. Nothing was charged.",

  // The account menu, the paywall and the agreement.
  "oauth.title": "Connect {client}",
  "oauth.lead": "{client} wants to use Pocket Studio as {who}. It can read your account, make and delete your applets, spend your credits and publish your games.",
  "oauth.back": "You go back to {host} after this.",
  "oauth.allow": "Connect",
  "oauth.deny": "Cancel",
  "oauth.signin": "Sign in to connect {client} to Pocket Studio.",
  "oauth.failed": "Could not connect. Try again.",
  "oauth.bad.title": "This link cannot connect",
  "oauth.connected": "Connected agents",
  "oauth.connected.lead": "Each of these can use Pocket Studio as you. Disconnect one and it has to ask again.",
  "oauth.connected.none": "Nothing is connected.",
  "oauth.connected.remove": "Disconnect",
  "oauth.connected.used": "Last used {date}",
  "oauth.connected.signin": "Sign in to see what is connected to your account.",
  "account.join": "Become a member",
  "account.manage": "Manage membership",
  "account.portalFailed": "Could not open the membership page. Try again.",
  "account.keep": "Keep this room and its games under an account.",
  "account.leave": "Leave",
  "account.signOut": "Sign out",
  "account.guest": "Guest",
  "account.plan": "Plan",
  "account.handle": "Handle",
  "account.handle.change": "Change your handle",
  "account.handle.rule": "Your games are signed with this name. Use lowercase letters, digits and hyphens, 3 to 24 of them.",
  "account.handle.failed": "Could not change the handle. Try again.",
  "plan.free": "Free",
  "plan.member": "Member",
  "plan.until": "Member until {day}",
  "plan.renews": "Member, renews {day}",
  // A membership an offer gave, and the page that claims an offer (src/ui/account.ts, src/core/offers.ts).
  "plan.life": "Member for life",
  "account.grantRenews": "You are a member for life, and your subscription still renews. Cancel it under Manage membership.",
  "offer.label": "Offer",
  "offer.signIn": "Sign in to claim this offer.",
  "offer.gives.life": "It gives a membership for life.",
  "offer.gives.days": "It gives a membership for {days} days.",
  "offer.ready.github": "The GitHub account linked to your Pocket Studio account is on this offer's list.",
  "offer.ready.code": "Type the code you were given.",
  "offer.claim": "Claim it",
  "offer.connect": "This offer is for the GitHub accounts on its list. Link your GitHub account to your Pocket Studio account to check.",
  "offer.connect.button": "Link GitHub",
  "offer.notListed": "The GitHub account linked to your Pocket Studio account is not on this offer's list.",
  "offer.used": "This was claimed for another Pocket Studio account.",
  "offer.code": "Code",
  "offer.wrongCode": "That code is not on this offer's list.",
  "offer.closed": "This offer has closed.",
  "offer.granted.life": "Your account is a member for life. Thank you.",
  "offer.granted.until": "Your account is a member until {day}.",
  "offer.missing": "There is no such offer.",
  "offer.failed": "Could not reach the offer. Try again.",
  "offer.linkedElsewhere": "That GitHub account is linked to another Pocket Studio account. Sign in with that account to claim the offer.",
  "offer.linkFailed": "Could not start linking GitHub. Try again.",
  "offer.done": "Close",
  "paywall.download": "Downloads are for members",
  "paywall.colours": "More colours are for members",
  "paywall.plan": "Plan",
  "paywall.perMonth": "{price} a month",
  "paywall.yearly": "{price} once a year",
  "paywall.monthly": "Paid every month",
  "paywall.terms": "It renews until you cancel under Manage membership in your account menu, and then lasts to the end of the time you paid for. See the {terms} and the {privacy}.",
  "paywall.go": "Continue to payment",
  "paywall.signIn": "A membership belongs to an account. Your room and games come with you.",
  "paywall.closed": "Membership is not open yet.",
  "paywall.failed": "Could not start the payment. Try again.",
  "paywall.mainland": "Pay from mainland China",
  "paywall.notNow": "Not now",
  "agreement.title": "Updated terms",
  "agreement.body": "Pocket Studio's {terms} have changed in a way that matters since you last agreed to them. Continuing with your account means you agree to the current version. The {privacy} says how we handle your information.",
  "agreement.go": "Agree and continue",
  "agreement.failed": "Could not save that. Try again.",
  "agreement.signOut": "Sign out instead",
  "legal.terms": "Terms",
  "legal.privacy": "Privacy Policy",
  "lang.label": "Language",

  // Signing in.
  "signin.title": "Sign in",
  "signin.unavailable": "Sign-in is not available here.",
  "signin.failed": "Could not sign in. Try again.",
  "signin.email": "Email address",
  "signin.emailPlaceholder": "you@example.com",
  "signin.sendCode": "Email me a code",
  "signin.google": "Continue with Google",
  "signin.github": "Continue with GitHub",
  "signin.guest": "Look around as a guest",
  "signin.code": "Six-digit code",
  "signin.sent": "We sent a code to {email}. It works for five minutes.",
  "signin.newest": "Enter the newest code we sent to {email}.",
  "signin.otherAddress": "Use another address",
  "signin.terms": "By continuing you agree to the {terms}. The {privacy} says how we handle your information.",
  "signin.offline": "Could not reach the studio. Check your connection.",
  "signin.tooMany": "Too many tries. Wait a minute and try again.",
  "signin.wrongCode": "That code is not right.",
  "signin.codeExpired": "That code has run out. Ask for a new one.",
  "signin.tooManyCodes": "Too many wrong codes. Ask for a new one.",
  "signin.badEmail": "That does not look like an email address.",

  // The colour palette.
  "palette.field": "{device}: saturation and brightness. Arrow keys adjust.",
  "palette.hue": "{device}: hue",
  "palette.code": "{device}: colour code",

  // A screen that takes no touch (src/core/touch.ts).
  "touch.hint": "{screen} does not take touch. {instead}",
  "touch.screen": "The {device}'s screen",
  "touch.upper": "The {device}'s upper screen",
  "touch.lower": "The {device}'s lower screen",
  "touch.useLower": "Touch the lower screen, or play with the keys.",
  "touch.useUpper": "Touch the upper screen, or play with the keys.",
  "touch.useKeys": "Play with the keys.",


  // The landing page (web/index.html).
  "landing.page.title": "Pocket Studio: made for the screens you hold",
  "landing.og.image": "https://studio.pocket.nexus/assets/og.jpg",
  "landing.og.alt": "Made for the screens you hold. Two handheld devices running what Pocket Studio makes.",
  "landing.title.html": "Made for the <span>screens you hold.</span>",
  "landing.lede": "Pocket Studio turns a few words into Pocket Applets that run even on MCUs, plus native Pocket3D games for handhelds. All on real hardware.",
  "landing.lede.html": "Pocket Studio turns a few words into <b>Pocket Applets</b> that run even on MCUs, plus native <b>Pocket3D</b> games for handhelds. All on real hardware.",
  "landing.prompt.example": "A desk weather board with the next five hours",
  "landing.prompt.note": "Free to try.",
  "landing.shelf": "Games",
  "landing.made.html": "Native games for handhelds, many of them <a href=\"https://3d.pocket.nexus/\">Pocket3D</a> worlds compiled for each machine. Play them here, or install them on yours.",
  "landing.room.title": "Made in a room of your own",
  "landing.room.text": "You make things in a small room in your browser. Tell the agent what you want, try each version on the screen beside the conversation, and download it when you like it. Pick up a handheld and play while it works.",
  "landing.room.alt": "The Studio's room: a conversation with the agent on the left, and the app it made running on a Tab5 screen beside it.",
  "shelf.native": "Native game",
  "shelf.mod": "Mod",
  "landing.prompt.label": "What should it do?",
  "landing.prompt.games": "Or play a game",
  "landing.open": "Open the studio",
  // The row of device chips on a card of the shelf: what the row is, and the chip for a desktop computer.
  "shelf.on": "Runs on",
  "shelf.desktop": "Desktop",
  "landing.mods.what": "Pocket Voxel and OpenStrike are mods. They read files from a game you own, so here they are shown as demos. The code is open: take it and make it yours.",
  "mods.voxel.line": "A Game Boy creature-RPG as a voxel diorama, built from your own ROM.",
  "mods.voxel.what": "Pocket Voxel rebuilds the towns, rooms and routes of a Game Boy creature-RPG as voxel dioramas, and plays the game in them.",
  "mods.voxel.how": "The game's state, with its battles, scripts, menus and saves, is TypeScript running on QuickJS, the PocketJS guest. A Rust core keeps only the scene: voxel chunks, sprites and the camera. The PSP draws that scene with the GE and the PS Vita with GXM, each through a renderer of its own.",
  "mods.voxel.why": "It runs on the ROM of a game you own, and what it builds from it stays on your machine. Here it is shown as a demo; the code is open for you to build and change.",
  "mods.voxel.from": "Drawn at the PSP's 480 × 272 by the software renderer the project checks the PSP's picture against.",
  "mods.strike.line": "A CS-like FPS on classic BSP maps from your own copy of the game.",
  "mods.strike.what": "OpenStrike is a first-person shooter of rounds against bots, played on classic BSP maps.",
  "mods.strike.how": "Its round rules, weapon tables, bot difficulty and the whole HUD are JavaScript running on QuickJS, with the HUD written in JSX. A Rust core simulates the world and draws it with a renderer for each machine: the GE on the PSP, GXM on the PS Vita, wgpu on a desktop computer.",
  "mods.strike.why": "It loads its maps and textures from a game you own and prepares them on your machine. Here it is shown as a demo; the code is open for you to build and change.",
  "mods.strike.from": "Recorded frame by frame from the PSP build in an emulator, at 480 × 272.",
  "mods.source": "Source on GitHub",
  "mods.close": "Close",

  // The index /games/ (server/listing.ts).
  "games.title": "Games",
  "games.lede": "Every game on the shelf. A game's page has its clips, a download for each device and its source.",
  "games.none": "No game is on the shelf yet.",

  // Every page's footer.
  "foot.elsewhere": "Elsewhere",
  "foot.mcp": "MCP",
  "foot.docs": "Docs",
  "foot.guide": "Install guide",
  "foot.credits": "Credits",
  "foot.terms": "Terms",
  "foot.privacy": "Privacy",
  "foot.marks": "PlayStation, PSP and PS Vita are trademarks of Sony Interactive Entertainment Inc. Nintendo 3DS and Game Boy are trademarks of Nintendo. iPod touch is a trademark of Apple Inc. Android and YouTube are trademarks of Google LLC. X, Discord, Hacker News, M5Stack and Tab5 belong to their owners. Pocket Nexus is not affiliated with, sponsored by or endorsed by any of them.",

  // The credits page (web/credits/index.html).
  "credits.title": "Credits",
  "credits.devices": "Devices",
  "credits.psp.html": "PSP model: “PlayStation Portable (PSP) - EG02” by <a href=\"https://sketchfab.com/3d-models/playstation-portable-psp-eg02-b76c7f9158204a39929a9c97d0b813d0\">Dibad</a>, <a href=\"https://creativecommons.org/licenses/by/4.0/\">CC BY 4.0</a>, optimized by PocketJS and drawn here without its logos and wordmarks. <a href=\"/devices/psp/ATTRIBUTION.md\">Details</a>.",
  "credits.vita.html": "PS Vita and Nintendo 3DS models: authored for <a href=\"https://pocketjs.pocket.nexus/\">PocketJS</a>; the PS Vita is drawn here without its logos and wordmarks. <a href=\"/devices/vita/ATTRIBUTION.md\">PS Vita</a>, <a href=\"/devices/3ds/ATTRIBUTION.md\">3DS</a>.",
  "credits.ipod.html": "iPod touch model: authored for Pocket Studio. <a href=\"/devices/ipod-touch/ATTRIBUTION.md\">Details</a>.",
  "credits.android.html": "Android phone model: authored for Pocket Studio, after no one maker's handset. <a href=\"/devices/android/ATTRIBUTION.md\">Details</a>.",
  "credits.marks": "PlayStation, PSP and PS Vita are trademarks of Sony Interactive Entertainment Inc. Nintendo 3DS is a trademark of Nintendo. iPod touch is a trademark of Apple Inc. Android is a trademark of Google LLC. The models depict the hardware for emulation, and the room draws none of these companies' logos. Pocket Nexus is not affiliated with, sponsored by or endorsed by any of them.",
  "credits.agents": "Agents",
  "credits.agents.text": "The two figures on the workbench show the coding agents the command-line tool can start. Claude and Claude Code are trademarks of Anthropic. Codex is a trademark of OpenAI. Pocket Studio is not affiliated with either company.",
  "credits.software": "Software",
  "credits.pocketjs.html": "<a href=\"https://pocketjs.pocket.nexus/\">PocketJS</a> runs every game and the laptop, MIT.",
  "credits.shell.html": "<a href=\"https://github.com/pocket-nexus/pocket-shell\">Pocket Shell</a> is the desktop on the laptop, GPL-3.0.",
  "credits.three.html": "<a href=\"https://threejs.org/\">three.js</a> draws the room, MIT.",
  "credits.type": "Type",
  "credits.fonts.html": "Gabarito by Naipe Foundry and IBM Plex by IBM, served from this site. Both are under the SIL Open Font License 1.1: <a href=\"/assets/fonts/Gabarito-OFL.txt\">Gabarito</a>, <a href=\"/assets/fonts/IBM-Plex-OFL.txt\">IBM Plex</a>.",
  "credits.noto.html": "Japanese headings are set in Noto Sans JP by Google and Adobe, served from this site as the characters these pages use, under the SIL Open Font License 1.1: <a href=\"/assets/fonts/Noto-Sans-JP-OFL.txt\">Noto Sans JP</a>.",
  "credits.titan": "The room's neon sign, its poster and the badge on the television are lettered in Titan One by Rodrigo Fuenzalida, under the same licence.",

  // The page of an address the site does not have (web/404.html).
  "notFound.title": "Not found",
  "notFound.heading": "That page is not in the room",
  "notFound.back": "Back to Pocket Studio",

  // The Terms, the Privacy Policy and the install guide's frame (scripts/legal.ts, scripts/help.ts).
  "legal.termsTitle": "Terms of Service",
  "legal.privacyTitle": "Privacy Policy",
  "legal.meta": "Last updated {date}. See also the {other}.",
  "guide.title": "Install guide",

  // A game's page (server/listing.ts).
  "game.by": "By {author}.",
  "game.fallback": "{title}, a game on Pocket Studio.",
  "game.imageAlt": "A picture from {title}.",
  "game.verified": "Verified by Pocket Nexus.",
  "game.homebrew": "Its packages are homebrew from the author, not tested by Pocket Nexus.",
  "game.open": "Open in Pocket Studio",
  "game.copy": "Copy link",
  "game.copied": "Copied",
  "game.post": "Post on X",
  "game.shows": "Clips and pictures",
  "game.clip": "Clip {n} of {count}: {caption}",
  "game.picture": "Picture {n} of {count}: {caption}",
  "game.cover": "The cover of {title}.",
  "game.download": "Download for {device}",
  "game.source.browser": "Recorded from the browser version.",
  "game.source.device": "Captured from the {device} build.",
  "game.note.browser": "These clips and pictures were recorded from the browser version. Each device draws the game with its own renderer, so the picture on it differs.",
  "game.note.devices": "These clips and pictures were captured from the game's builds for the devices.",
  "game.note.mixed": "Each device draws the game with its own renderer, so its picture differs from the browser version's.",

  // What a game offers wherever it is shown: Play, Download, Remix (src/core/icons.ts, src/gate/).
  "act.play": "Play",
  "act.download": "Download",
  "act.remix": "Remix",
  "act.play.of": "Play {title} in your browser",
  "act.download.of": "Download {title}",
  "act.remix.of": "Remix {title}",
  "get.heading": "Download",
  "get.title": "Download {title}",
  "get.devices": "For your device",
  "get.source": "Source code",
  "get.source.zip": "ZIP from {host}",
  "get.source.repo": "Repository on {host}",
  "get.source.archive": "Source archive",
  "get.source.note": "Every package here is built from this source, and a remix starts from it.",
  "get.version": "Version {version}",
  "get.free": "Free with a Pocket Studio account.",
  "get.guide": "Install guide",
  "get.none": "This game has no packages to download yet.",
  "get.versions": "All versions",
  "get.versions.note": "Past versions come with the membership.",
  "get.signin": "Sign in to download {title} for {device}. It is free.",
  "get.signin.source": "Sign in to download the source of {title}. It is free.",
  "get.started": "{title} for {device} is downloading.",
  "get.started.source": "The source of {title} is downloading.",
  "get.again": "Download again",
  "get.back": "Back to the game",
  "get.close": "Close",
  "versions.title": "All versions of {title}",
  "versions.current": "Current",
  "versions.none": "No earlier versions are kept yet. When this game is updated, the versions before it stay here.",
  "versions.member": "Past versions come with the membership.",
  "versions.signin": "Sign in to get past versions of {title}.",
  "paywall.versions": "Past versions are for members",
  "remix.page.lead": "Paste this into your coding agent. It copies the game to your computer and changes it into a game of your own.",
  "remix.any": "Write what you want to change, then send it. It works in any coding agent that runs commands on your computer, such as Claude Code, Codex or Cursor.",
  "remix.publish": "Publish it on Pocket Studio",
  "remix.publish.text": "Your remix gets a page of its own, and this game's page lists it under Remixes. Ask your agent to publish it. It asks you for a link code.",
  "remix.publish.pocket": "Your agent asks you for a link code when it copies the game. Once you publish the remix, it gets a page of its own and this game's page lists it under Remixes.",
  "remix.code": "Get a link code",
  "remix.code.note": "Give this code to your agent. It links the pocket-studio command on your computer to your account, once, within 15 minutes.",
  "remix.signin": "Sign in to get a link code. It is free.",
  "remix.guide": "How to publish a remix",
  "remix.code.title": "Link code",
  "remix.guide.pocket": "Remix guide",
  "remix.engine": "Pocket3D on GitHub",
  "game.remixOf": "A remix of {game} by {author}.",
  "game.remixes": "Remixes",
  "game.remixes.more": "And {count} more.",
  "game.remix.by": "By {author}",
  "game.remix.browser": "Plays in a browser",
  "feed.label": "Other games",
  "feed.next": "Next game",
  "feed.previous": "Previous game",
  "dock.about": "About {title}",

  // The Studio's own words in the web player of a published Pocket app (src/play/).
  "play.made": "Made with Pocket Studio",
  "play.without": "{title} is a PocketJS app by {author}, built for {devices}. It has no packages to download yet.",
  "play.without.alone": "{title} is a PocketJS app by {author}. It has no packages to download yet.",
  "play.about": "This is the PocketJS web player.",
  "play.missing": "This game is not here.",
  "play.noBuild": "This game has no build to play yet.",
  "play.failed": "The game could not start.",
  "play.stopped": "The game stopped. Reload to start it again.",

  // The Studio's own small pages on a game host (server/site.ts).
  "host.open": "Open Pocket Studio",
  "host.openIt": "Open it in Pocket Studio",
  "host.by": "By {author}",
  "host.none.title": "No game here",
  "host.none": "No published game has this address.",
  "host.missing.title": "Not found",
  "host.missing": "{title} has no page at this address.",
  "host.empty.title": "Nothing here yet",
  "host.empty": "{title} has no site yet.",

  // The sign-in code's mail.
  "mail.subject": "Your Pocket Nexus sign-in code",
  "mail.text": "Your sign-in code is {otp}. It expires in 5 minutes.",
  "mail.html": "<p>Your sign-in code is <strong>{otp}</strong>.</p><p>It expires in 5 minutes.</p>",

  // The limits on sign-in codes (src/core/codes.ts).
  "codes.address": "We sent {count} codes to that address in {minutes} minutes. Use the newest one, or ask again in {wait}.",
  "codes.day": "We sent {count} codes to that address in a day. Ask again in {wait}.",
  "codes.network": "Too many codes were asked for from your network. Ask again in {wait}.",
  "codes.wait.minute": "1 minute",
  "codes.wait.minutes": "{n} minutes",
  "codes.wait.hours": "about {n} hours",

  // Refusals of the API that the room shows (server/refusals.ts).
  "api.title.text": "Title must be text",
  "api.title.length": "Title must be 1 to 60 characters",
  "api.takenDown": "Pocket Nexus took this game down. It cannot be published until that is lifted. Write to {email}.",
  "api.publish.site": "Deploy the site or upload a package before publishing",
  "api.publish.package": "Upload a package before publishing",
  "api.publish.build": "Upload a build before publishing",
  "api.guest.address": "Sign in with an account to give a game its own address",
  "api.guest.handle": "Sign in with an account to choose a handle",
  "api.app.notFound": "App not found",
  "api.app.published": "Published app not found",
  "api.session.notFound": "Session not found",
  "api.signInFirst": "Sign in first",
  "api.busy": "Busy. Try again.",
  "api.rooms.soon": "More rooms are coming soon.",
  "api.download.off": "The author has not enabled native downloads",
  "api.download.none": "The author has not uploaded an installation package for this device yet",
  "api.remix.no": "This game cannot be remixed",
  "api.remix.noSource": "The game this remix starts from has no source to give",
  "api.projects.limit": "An account holds at most {max} projects. Delete one to make another.",
  "api.slug.length": "An address is {min} to {max} characters",
  "api.slug.chars": "An address uses lowercase letters, digits and single hyphens between them",
  "api.slug.reserved": "That address is reserved",
  "api.slug.taken": "The address {slug} is taken",
  "api.slug.held": "The address {slug} was given up less than {days} days ago and is held for the account that had it",
  "api.handle.length": "A handle is {min} to {max} characters",
  "api.handle.chars": "A handle uses lowercase letters, digits and single hyphens between them",
  "api.handle.reserved": "That handle is reserved",
  "api.handle.taken": "The handle {handle} is taken",

  "api.make.busy": "The maker is busy right now. Your credits are back. Try again in a minute.",
  "api.make.daily": "The maker has made all it can today. Try again tomorrow.",
  "api.make.credits": "You do not have enough credits for this.",
  "api.make.guest": "Sign in with an account to make an app",
  "api.make.empty": "Say what to make",
  "api.make.notFound": "App not found",
  "api.make.noTab5": "Making Tab5 apps is not open yet",
  "api.make.tab5Working": "The agent is still working on this app.",
  "api.make.tab5NoVersion": "That version is not there",

  // Making an app in the room (src/ui/make.ts, docs/make.md).
  "make.dock.label": "Make an app with an agent",
  "make.dock.send": "Make it",
  "make.dock.for": "Apps for the M5Stack Tab5",
  "make.dock.free": "Free to try: new accounts get {n} credits",
  "make.credits": "Credits: {n}",
  "make.loading": "One moment",
  "make.close": "Close",
  "make.failed": "Something went wrong. Try again.",
  "make.chat.label": "Your conversation with the maker",
  "make.send": "Send",
  "make.send.price": "Send · {n}",
  "make.error.fix": "Fix it · {n}",
  "make.error.ignore": "Ignore",
  "make.restart": "Restart",
  "make.fullscreen": "Full screen",
  "make.tab5.on": "Tab5 app: describe it, and an agent makes it",
  "make.tab5.example": "A focus timer with one big button",
  "make.tab5.mine": "Your Tab5 apps",
  "make.tab5.draft": "Test build, not saved yet",
  "make.tab5.draft.short": "Test",
  "make.tab5.draft.step": "A test build is on the screen.",
  "make.tab5.screen.starting": "Starting the app",
  "make.tab5.input.first": "Describe what you want to make",
  "make.tab5.input.next": "Ask for a change",
  "make.tab5.error": "The app hit an error: {message}",
  "make.tab5.new": "New Tab5 app",
  "make.tab5.hello": "Tell me what you want on the Tab5: a tool, a toy, a game. It is a wide touch screen held sideways, with no buttons.",
  "make.tab5.working": "The agent is working on your app.",
  "make.tab5.stop": "Stop",
  "make.tab5.stopped": "The agent stopped before it finished. Your credits are back.",
  "make.tab5.step.read": "Reading {file}",
  "make.tab5.step.write": "Writing {file}",
  "make.tab5.step.search": "Looking through the code",
  "make.tab5.step.docs": "Reading the PocketJS docs",
  "make.tab5.step.build": "Building the app",
  "make.tab5.step.shot": "Trying the app to check it",
  "make.tab5.step.run": "Running a command",
  "make.tab5.step.save": "Saving a version",
  "make.tab5.saved": "Version {rev} is ready to try.",
  "make.tab5.screen.empty": "The app appears here once the agent builds it.",
  "make.tab5.version": "Version {rev}",
  "make.tab5.device": "Download for Tab5",
  "make.tab5.device.lead": "The package of what is on screen: the file a Tab5 installs.",
  "make.out": "This takes {price} credits, and you have {balance}.",
  "make.out.member": "Members get {n} credits each month.",
  "make.out.paused": "More credits come with the membership when it opens again.",
  "make.out.renews": "Your credits refill on {date}.",

} as const;

export type MessageKey = keyof typeof EN;
